import { CarrierVisitDto } from '@operations/app/api'
import { useTranslate } from '@tolgee/react'
import { formatVesselVisitIdentifier, PATHS } from '@tom-ui/utils'
import { BreadcrumpItem } from 'modules/ui/src/theme/components/breadcrumbs/Breadcrumb'
import { useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router'

export const useVesselVisitBreadcrumbs = (vesselVisit?: CarrierVisitDto) => {
  const { t } = useTranslate()
  const navigate = useNavigate()

  const { pathname } = useLocation() as {
    pathname: string
  }

  const path = pathname.split('/').filter(Boolean)

  const isVesselVisitHatchCoverMovesRoute = path.includes(PATHS.visits.vesselVisits.hatchCovers.url)
  const isCraneSplitRoute = path.includes(PATHS.visits.vesselVisits.craneSplit.url)
  const isEquipmentsRoute = path.includes(PATHS.visits.vesselVisits.equipments.url)
  const isStoppagesRoute = path.includes(PATHS.visits.vesselVisits.stoppages.url)
  const isKPIRoute = path.includes(PATHS.visits.vesselVisits.kpi.url)
  const isWorkInstructionsRoute = path.includes(PATHS.controlRoom.jobs.url.split('/')[1] || '')

  const carrierVisitLabel = useMemo(() => {
    if (!vesselVisit) return
    return formatVesselVisitIdentifier(vesselVisit.carrierName, vesselVisit.vesselVisitNumber)
  }, [vesselVisit])

  const thirdLevelLabel = useMemo(() => {
    if (isVesselVisitHatchCoverMovesRoute) return t('hatchCoverMoves', 'Hatch cover moves')
    if (isCraneSplitRoute || isWorkInstructionsRoute) return t('craneSplits', 'Crane splits')
    if (isEquipmentsRoute) return t('equipmentPlanning', 'Equipment Planning')
    if (isStoppagesRoute) return t('stoppage', 'Stoppage')
    if (isKPIRoute) return t('vesselVisitsKPI', 'Vessel visits KPI')
  }, [
    isCraneSplitRoute,
    isEquipmentsRoute,
    isKPIRoute,
    isStoppagesRoute,
    isVesselVisitHatchCoverMovesRoute,
    isWorkInstructionsRoute,
    t,
  ])

  const fourthLevelLabel = useMemo(() => {
    if (isWorkInstructionsRoute) return t('workInstructions', 'Work Instructions')
  }, [isWorkInstructionsRoute, t])

  const breadcrumbs = useMemo(() => {
    const basePath = PATHS.visits.vesselVisits.url
    const baseLabel = t('vesselVisits', 'Vessel visits')

    const carrierVisitDashboardUrl = `${basePath}/${vesselVisit?.id}/dashboard`
    const craneSplitUrl = `${basePath}/${vesselVisit?.id}/${PATHS.visits.vesselVisits.craneSplit.url}`

    const breadcrumbs: BreadcrumpItem[] = [
      {
        label: baseLabel,
        onClick: () => navigate(basePath),
      },
    ]

    if (carrierVisitLabel) {
      breadcrumbs.push({
        label: carrierVisitLabel,
        onClick: () => (thirdLevelLabel ? navigate(carrierVisitDashboardUrl) : undefined),
      })
    }

    if (thirdLevelLabel) {
      breadcrumbs.push({
        label: thirdLevelLabel,
        onClick: () => (fourthLevelLabel ? navigate(craneSplitUrl) : undefined),
      })
    }

    if (fourthLevelLabel) {
      breadcrumbs.push({
        label: fourthLevelLabel,
      })
    }

    return breadcrumbs
  }, [carrierVisitLabel, fourthLevelLabel, navigate, t, thirdLevelLabel, vesselVisit?.id])

  return { breadcrumbs }
}
